import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Plus, Search, Pin, NotebookPen, CalendarHeart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtDateTime } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { Panel, EmptyState } from "@/components/core/AppLayout";
import { NotePanel, newNote } from "@/components/notes/NotePanel";

const LinkedEvents = ({ clientId }) => {
  const { data } = useFetch(`/me/eventi?client_id=${clientId}`);
  const navigate = useNavigate();
  return (
    <Panel className="p-5" data-testid="client-notes-events">
      <h4 className="font-display font-extrabold text-[#0A1128] flex items-center gap-2 mb-3"><CalendarHeart className="h-4 w-4 text-[#E8173A]" />I miei eventi personali collegati</h4>
      {data && !data.length && <div className="text-sm text-slate-600">Nessun evento personale collegato a questo cliente.</div>}
      {(data || []).map((e) => (
        <button key={e.id} onClick={() => navigate(`/il-mio-calendario?evento=${e.id}`)} className="w-full text-left flex items-center gap-2 rounded-xl px-2 py-2 hover:bg-[#A6DAFC]" data-testid={`client-notes-event-${e.id}`}>
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.colore }} />
          <span className="font-bold text-sm text-[#0A1128] truncate flex-1">{e.titolo}</span>
          <span className="text-xs text-slate-600 shrink-0">{e.all_day ? fmtDate(e.inizio) : fmtDateTime(e.inizio)}</span>
        </button>
      ))}
    </Panel>
  );
};

export const ClientNotes = ({ client }) => {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  const { data: notes, setData } = useFetch(`/me/clienti/${client.id}/appunti${query ? `?q=${encodeURIComponent(query)}` : ""}`);
  const [panel, setPanel] = useState(null);
  const sorted = [...(notes || [])].sort((a, b) => (b.pinned - a.pinned) || b.data.localeCompare(a.data));
  const upsert = (n) => setData((p) => [...(p || []).filter((x) => x.id !== n.id), n]);

  return (
    <div className="grid grid-cols-[1fr_340px] gap-6" data-testid="client-notes">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white px-3 py-1 text-xs font-bold" data-testid="client-notes-private-label"><Lock className="h-3.5 w-3.5" />Visibile solo a te</span>
          <div className="relative flex-1 max-w-sm"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca negli appunti" className="rounded-full pl-9" data-testid="client-notes-search" /></div>
          <Button onClick={() => setPanel({ key: `new-${Date.now()}`, note: newNote() })} className="ml-auto rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="client-notes-new"><Plus className="h-4 w-4 mr-1" />Nuovo appunto</Button>
        </div>
        {notes && !sorted.length ? <Panel className="p-6"><EmptyState icon={NotebookPen} title={query ? "Nessun appunto trovato" : "Nessun appunto"} text="Gli appunti che scrivi qui sono visibili solo a te." testid="client-notes-empty" /></Panel> : (
          <div className="space-y-3">
            {sorted.map((n) => (
              <button key={n.id} onClick={() => setPanel({ key: `${n.id}-${Date.now()}`, note: n })} className="w-full text-left rounded-2xl border border-[#5FB3EA] bg-[#8FD0FB] hover:bg-[#A6DAFC] p-5 transition-colors" data-testid={`client-note-${n.id}`}>
                <div className="flex items-center gap-2">
                  {n.pinned && <Pin className="h-4 w-4 text-[#E8173A]" data-testid={`client-note-pinned-${n.id}`} />}
                  <span className="font-display font-extrabold text-[#0A1128] flex-1 truncate">{n.titolo}</span>
                  <span className="text-xs font-bold text-slate-600">{fmtDate(n.data)}</span>
                </div>
                {n.body_text && <p className="text-sm text-slate-700 mt-1.5 line-clamp-2">{n.body_text}</p>}
              </button>
            ))}
          </div>
        )}
      </div>
      <LinkedEvents clientId={client.id} />
      <NotePanel panel={panel} clientId={client.id} onClose={() => setPanel(null)} onChanged={upsert} onDeleted={(id) => setData((p) => p.filter((x) => x.id !== id))} />
    </div>
  );
};
